import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Table,
  Tooltip,
  makeStyles,
  TableSortLabel,
} from '@material-ui/core';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import Excel from 'assets/icons/svg/Excel';
import { getBackgroundColorByRatingSum } from '../../../helpers';
import { PartnerRatingDialog } from '../../../components';
import { useExportPartnerRatingByPartnerExcelReport } from '../../../hooks';
import { IPartnerRatingByRealEstate, OrderColumn } from '../interfaces';

const useStyles = makeStyles({
  tableHeadCell: {
    backgroundColor: '#f5f5f5',
  },
  tableHeadCellSticky: {
    position: 'sticky',
    backgroundColor: '#f5f5f5',
  },
  tableBodyRow: {
    cursor: 'pointer',

    '&:nth-of-type(odd) td': {
      backgroundColor: '#f9f9f9',
    },
    '&:nth-of-type(even) td': {
      backgroundColor: '#fff',
    },
  },
  tableBodyCellSticky: {
    position: 'sticky',
  },
});

interface IProps {
  isActivePartner: boolean;
  data: { list: IPartnerRatingByRealEstate[]; totalCount: number };
  year: number;
  loading: boolean;
  success: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  exportExcelLoading: boolean;
  orderColumn: OrderColumn;
  onExportExcel(): void;
  onChangeOrderColumn(orderColumn: OrderColumn): void;
  onChangePage(pageNo: number): void;
}

const PartnersRating = ({
  isActivePartner,
  year,
  data,
  error,
  success,
  loading,
  page,
  pageSize,
  exportExcelLoading,
  orderColumn,
  onChangeOrderColumn,
  onChangePage,
  onExportExcel,
}: IProps) => {
  // Styles
  const classes = useStyles();

  const [selectedPartnerRating, setSelectedPartnerRating] = useState<IPartnerRatingByRealEstate | null>(null);
  const [partnerRatingDialogOpened, setOpenedPartnerRatingDialog] = useState(false);

  const { exportPartnerRatingByPartnerExcelReport, ...partnerRatingByPartnerExcelState } =
    useExportPartnerRatingByPartnerExcelReport();

  const params = {
    partnerGuid: selectedPartnerRating?.partnerGuid,
    serviceTypeId: selectedPartnerRating?.serviceTypeId,
    year,
  };

  function handleClickPartnerRating(partnerRating: IPartnerRatingByRealEstate) {
    setSelectedPartnerRating(partnerRating);
    setOpenedPartnerRatingDialog(true);
  }

  function handleClosePartnerRatingDialog() {
    setSelectedPartnerRating(null);
    setOpenedPartnerRatingDialog(false);
  }

  return (
    <>
      {Boolean(loading) ? (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : Boolean(error) && !Boolean(data.list.length) ? (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Typography component="h3" variant="h5" align="center">
            {error}
          </Typography>
        </Box>
      ) : (
        success && (
          <>
            {data.list.length ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {(
                        [
                          ['realEstateName', 'Объект недвижимости', 220, 0, isActivePartner],
                          ['serviceTypeName', 'Тип услуг', 160, 220, isActivePartner],
                          ['partnerName', 'Партнер', 150, 380, true],
                        ] as const
                      ).map(([columnName, label, width, offset, sortable]) => (
                        <Box
                          key={label}
                          component={TableCell}
                          className={classes.tableHeadCellSticky}
                          style={{ minWidth: width, left: offset }}
                        >
                          {sortable ? (
                            <TableSortLabel
                              active={orderColumn.columnName === columnName}
                              direction={
                                orderColumn.columnName === columnName && orderColumn.sortType === 'ASC' ? 'desc' : 'asc'
                              }
                              onClick={() =>
                                onChangeOrderColumn({
                                  columnName,
                                  sortType:
                                    orderColumn.columnName === columnName && orderColumn.sortType === 'ASC'
                                      ? 'DESC'
                                      : 'ASC',
                                })
                              }
                            >
                              <b>{label}</b>
                            </TableSortLabel>
                          ) : (
                            <b>{label}</b>
                          )}
                        </Box>
                      ))}

                      <Box
                        component={TableCell}
                        className={classes.tableHeadCellSticky}
                        style={{ minWidth: 150, left: 530, textAlign: 'center' }}
                      >
                        <b>Срок сотрудничества</b>
                      </Box>

                      {(
                        [
                          ['Текущая оценка', 150, 680],
                          ['Средняя оценка', 120, 830],
                        ] as const
                      ).map(([label, width, offset]) => (
                        <Box
                          component={TableCell}
                          className={classes.tableHeadCellSticky}
                          key={label}
                          style={{ minWidth: width, left: offset, textAlign: 'center' }}
                        >
                          <b>{label}</b>
                        </Box>
                      ))}

                      {['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'].map(
                        (month) => (
                          <TableCell
                            key={month}
                            className={classes.tableHeadCell}
                            style={{ minWidth: 100 }}
                            align="center"
                          >
                            <b>{month}</b>
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data.list.map((partnerRating, index) => {
                      const {
                        ratingSum,
                        realEstateName,
                        realEstateGuid,
                        serviceTypeName,
                        partnerName,
                        months,
                        monthCount,
                        lastRatingSum,
                        partnerGuid,
                      } = partnerRating;

                      return (
                        <TableRow
                          className={classes.tableBodyRow}
                          key={`${realEstateGuid}:${partnerGuid}:${index}`}
                          hover
                          onClick={() => handleClickPartnerRating(partnerRating)}
                        >
                          {(
                            [
                              [realEstateName, 0],
                              [serviceTypeName, 220],
                              [partnerName, 380],
                            ] as const
                          ).map(([value, offset], index) => (
                            <TableCell className={classes.tableBodyCellSticky} key={index} style={{ left: offset }}>
                              {value}
                            </TableCell>
                          ))}

                          <TableCell className={classes.tableBodyCellSticky} style={{ left: 530, textAlign: 'center' }}>
                            {monthCount}
                          </TableCell>

                          {(
                            [
                              [lastRatingSum, 680],
                              [ratingSum, 830],
                            ] as const
                          ).map(([value, offset], index) => (
                            <TableCell
                              key={index}
                              className={classes.tableBodyCellSticky}
                              style={{
                                backgroundColor: getBackgroundColorByRatingSum(value),
                                left: offset,
                                textAlign: 'center',
                              }}
                            >
                              {Math.round(value * 100) / 100}
                            </TableCell>
                          ))}

                          {[...Array(12)].map((_, index) => {
                            const month = months.find((month) => +month.dateAt.split('-')[1] === index + 1);

                            return month ? (
                              <TableCell
                                style={{
                                  backgroundColor: getBackgroundColorByRatingSum(month.ratingSum),
                                  textAlign: 'center',
                                }}
                                key={month.dateAt}
                              >
                                <b>{Math.round(month.ratingSum * 100) / 100}</b>
                              </TableCell>
                            ) : (
                              <TableCell key={index} style={{ textAlign: 'center' }}>
                                0
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Typography component="h3" variant="h5" align="center">
                  Данные отсутствуют
                </Typography>
              </Box>
            )}

            <PartnerRatingDialog
              partnerGuid={selectedPartnerRating?.partnerGuid ?? ''}
              partnerName={selectedPartnerRating?.partnerName ?? ''}
              partnersCount={data.totalCount}
              year={year}
              {...(selectedPartnerRating && {
                serviceType: {
                  serviceTypeId: selectedPartnerRating.serviceTypeId,
                  serviceTypeName: selectedPartnerRating.serviceTypeName,
                },
              })}
              isActivePartner={isActivePartner}
              open={partnerRatingDialogOpened}
              exportExcelLoading={partnerRatingByPartnerExcelState.loading}
              onExportExcel={() =>
                exportPartnerRatingByPartnerExcelReport(selectedPartnerRating!.partnerGuid, { params })
              }
              onClose={handleClosePartnerRatingDialog}
            />
          </>
        )
      )}

      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Tooltip title="Скачать отчет в Excel">
          <span>
            <IconButton
              aria-label="Скачать отчет в Excel"
              onClick={onExportExcel}
              disabled={loading || exportExcelLoading}
            >
              <Excel style={{ color: '#3C763D' }} />
            </IconButton>
          </span>
        </Tooltip>

        <IconButton aria-label="Назад" disabled={loading || page === 1} onClick={() => onChangePage(page - 1)}>
          <KeyboardArrowLeft />
        </IconButton>

        <IconButton
          aria-label="Вперёд"
          disabled={loading || !data.totalCount || page === Math.ceil((data.totalCount || 0) / pageSize)}
          onClick={() => onChangePage(page + 1)}
        >
          <KeyboardArrowRight />
        </IconButton>
      </Box>
    </>
  );
};

export default PartnersRating;
